import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LegalPages.module.css';

const TermsOfServicePage = () => {
  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <Link to="/" className={styles.backLink}>&larr; Back to Home</Link>

        <h1 className={styles.title}>Terms of Service</h1>
        <p className={styles.effectiveDate}>Effective Date: February 21, 2026</p>

        {/* 1. Agreement */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Agreement to Terms</h2>
          <p className={styles.paragraph}>
            By accessing or using Teachio ("the Service"), operated by REEC Holdings, you agree to be
            bound by these Terms of Service. If you do not agree, do not use the Service.
          </p>
          <p className={styles.paragraph}>
            You must be at least 18 years of age to create an account and use Teachio. By registering,
            you represent that you are 18 or older.
          </p>
        </div>

        {/* 2. Description */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>2. Description of Service</h2>
          <p className={styles.paragraph}>
            Teachio is a free, AI-powered lesson planning tool for K-12 educators. The Service allows
            teachers to create lesson plans through conversation with an AI assistant, align lessons to
            Common Core State Standards (CCSS), organize lessons on a calendar, and manage class
            scope and sequence.
          </p>
        </div>

        {/* 3. Account */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>3. Account Responsibilities</h2>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              You must provide a valid email address when registering.
            </li>
            <li className={styles.listItem}>
              You are responsible for maintaining the security of your password and account.
            </li>
            <li className={styles.listItem}>
              You may not share your account credentials with others.
            </li>
            <li className={styles.listItem}>
              You are responsible for all activity that occurs under your account.
            </li>
            <li className={styles.listItem}>
              You agree to notify us promptly of any unauthorized access to your account.
            </li>
          </ul>
        </div>

        {/* 4. Acceptable Use */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>4. Acceptable Use</h2>
          <p className={styles.paragraph}>
            Teachio is intended for educational lesson planning purposes. You agree not to:
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              Use automated tools to scrape, crawl, or extract data from the Service.
            </li>
            <li className={styles.listItem}>
              Attempt to circumvent rate limits or security measures.
            </li>
            <li className={styles.listItem}>
              Upload or generate content that is illegal, harmful, or violates the rights of others.
            </li>
            <li className={styles.listItem}>
              Use the Service to generate content unrelated to education or lesson planning.
            </li>
            <li className={styles.listItem}>
              Interfere with or disrupt the Service or its infrastructure.
            </li>
            <li className={styles.listItem}>
              Impersonate another person or misrepresent your affiliation with any entity.
            </li>
          </ul>
        </div>

        {/* 5. Intellectual Property */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>5. Intellectual Property</h2>
          <p className={styles.paragraph}>
            <strong>Your content:</strong> You retain ownership of all lesson plans, class information,
            and other educational content you create using Teachio. We do not claim any intellectual
            property rights over your content.
          </p>
          <p className={styles.paragraph}>
            <strong>Our platform:</strong> The Teachio platform, including its design, code, branding,
            and AI integrations, is the property of REEC Holdings. You may not copy, modify, or
            distribute any part of the platform without our written permission.
          </p>
          <p className={styles.paragraph}>
            <strong>AI-generated content:</strong> Lesson plans and other content generated by the AI
            assistant are provided to you for your use. You are free to use, modify, and distribute
            your AI-generated lesson plans as you see fit.
          </p>
        </div>

        {/* 6. AI Disclaimer */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>6. AI-Generated Content Disclaimer</h2>
          <p className={styles.paragraph}>
            Teachio uses artificial intelligence to assist in creating lesson plans. While we strive
            for accuracy and quality, AI-generated content may contain errors, inaccuracies, or
            omissions. It is your responsibility as an educator to review, verify, and adapt all
            AI-generated content before using it in your classroom.
          </p>
          <p className={styles.paragraph}>
            Teachio does not guarantee that AI-generated lesson plans will meet specific educational
            standards, curriculum requirements, or institutional policies. The AI assistant is a tool
            to support your professional judgment, not replace it.
          </p>
        </div>

        {/* 7. Privacy */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>7. Privacy</h2>
          <p className={styles.paragraph}>
            Your use of Teachio is also governed by our{' '}
            <Link to="/privacy" className={styles.inlineLink}>Privacy Policy</Link>, which describes
            how we collect, use, and protect your personal information. By using the Service, you
            consent to the data practices described in our Privacy Policy.
          </p>
        </div>

        {/* 8. Service Availability */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>8. Service Availability</h2>
          <p className={styles.paragraph}>
            Teachio is provided "as is" and "as available." We do not guarantee uninterrupted or
            error-free access to the Service. We reserve the right to modify, suspend, or discontinue
            the Service (or any part of it) at any time, with or without notice.
          </p>
          <p className={styles.paragraph}>
            We may perform maintenance, updates, or changes that temporarily affect availability. We
            will make reasonable efforts to notify users of planned downtime when possible.
          </p>
        </div>

        {/* 9. Limitation of Liability */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>9. Limitation of Liability</h2>
          <p className={styles.paragraph}>
            To the fullest extent permitted by law, REEC Holdings and its officers, directors, and
            employees shall not be liable for any indirect, incidental, special, consequential, or
            punitive damages arising from your use of (or inability to use) the Service.
          </p>
          <p className={styles.paragraph}>
            Because Teachio is a free service, our total aggregate liability for any claims arising
            from your use of the Service is limited to the amount you have paid us, which is zero ($0).
          </p>
          <p className={styles.paragraph}>
            We are not liable for any decisions you make based on AI-generated content, including
            lesson plans, standards alignments, or scope and sequence recommendations.
          </p>
        </div>

        {/* 10. Indemnification */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>10. Indemnification</h2>
          <p className={styles.paragraph}>
            You agree to indemnify and hold harmless REEC Holdings, its officers, directors, and
            employees from any claims, damages, losses, or expenses (including reasonable attorneys'
            fees) arising from your use of the Service, your violation of these Terms, or your
            violation of any rights of a third party.
          </p>
        </div>

        {/* 11. Termination */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>11. Termination</h2>
          <p className={styles.paragraph}>
            <strong>By you:</strong> You may delete your account at any time through your account
            settings. Upon deletion, all your data will be permanently removed as described in our
            Privacy Policy.
          </p>
          <p className={styles.paragraph}>
            <strong>By us:</strong> We reserve the right to suspend or terminate your account if you
            violate these Terms, engage in abusive behavior, or use the Service in a manner that could
            harm other users or our infrastructure.
          </p>
        </div>

        {/* 12. Governing Law */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>12. Governing Law</h2>
          <p className={styles.paragraph}>
            These Terms shall be governed by and construed in accordance with the laws of the State
            of Illinois, without regard to its conflict of law provisions. Any disputes arising under
            these Terms shall be subject to the exclusive jurisdiction of the courts located in Cook
            County, Illinois.
          </p>
        </div>

        {/* 13. Severability */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>13. Severability & Entire Agreement</h2>
          <p className={styles.paragraph}>
            If any provision of these Terms is found to be unenforceable, the remaining provisions
            will continue in full force and effect. These Terms, together with our Privacy Policy and
            Cookie Policy, constitute the entire agreement between you and REEC Holdings regarding
            your use of Teachio.
          </p>
        </div>

        {/* 14. Contact */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>14. Contact</h2>
          <p className={styles.paragraph}>
            If you have questions about these Terms of Service, contact us:
          </p>
          <div className={styles.contactInfo}>
            <p>
              REEC Holdings<br />
              4933 W 106th PL.<br />
              Oak Lawn, IL 60453<br />
              Email: privacy@teachio.ai
            </p>
          </div>
        </div>

        {/* Cross-links */}
        <div className={styles.legalNav}>
          <Link to="/privacy" className={styles.legalNavLink}>Privacy Policy</Link>
          <Link to="/cookies" className={styles.legalNavLink}>Cookie Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
